import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { api, errText, media } from "../lib/api";

const STATE_TEXT = {
  used: "Cette commande est déjà acceptée. Merci !",
  withdrawn: "Cette commande vous a été retirée et confiée à un autre partenaire. Aucune action n'est nécessaire.",
  closed: "Cette commande n'attend plus d'acceptation.",
};

// One-tap acceptance page reached from the WhatsApp / e-mail / SMS link. No login.
export default function AcceptOrder() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/accept/${token}`).then((r) => setData(r.data)).catch((e) => setErr(errText(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/accept/${token}`);
      setData((d) => ({ ...d, state: "used" }));
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4" data-testid="accept-page">
      <div className="bg-plaster w-full max-w-md p-6 space-y-4">
        <p className="smallcaps">Fleurinelle · Commande partenaire</p>
        {!data && !err ? <p className="text-sm text-muted flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</p> : null}
        {err && !data ? <p className="text-sm text-danger" data-testid="accept-error">{err}</p> : null}
        {data ? (
          <>
            <h1 className="font-display text-2xl text-ink">{data.order_number}</h1>
            <p className="text-sm text-muted">Bonjour {data.owner_name || data.shop_name} — livraison à <b>{data.city}</b> le <b>{data.delivery_date}</b> · {data.delivery_window}.</p>
            <div className="space-y-2" data-testid="accept-items">
              {data.items.map((i, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  {i.image ? <img src={media(i.image)} alt="" className="w-12 aspect-[4/5] object-cover" /> : null}
                  <span className="text-sm text-ink">{i.quantity} × {i.title}{i.variant ? ` · ${i.variant}` : ""}</span>
                </div>
              ))}
              {data.addons.map((a, idx) => <p key={idx} className="text-xs text-muted">+ {a.quantity} × {a.name}</p>)}
            </div>
            <p className="text-sm text-ink">Votre part : <b className="tabular">{data.payout_mad} MAD</b></p>
            {data.state === "open" ? (
              <>
                {data.acceptance ? (
                  <p className={`text-xs flex items-center gap-1 ${data.acceptance.overdue ? "text-danger" : "text-muted"}`} data-testid="accept-clock">
                    <Clock size={12} /> {data.acceptance.working_minutes} min ouvrées écoulées{data.acceptance.overdue ? " — en retard, merci de confirmer" : ""}
                  </p>
                ) : null}
                <button className="btn-primary w-full inline-flex items-center justify-center gap-2" disabled={busy} onClick={accept} data-testid="accept-button">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} J'accepte cette commande
                </button>
                <p className="text-[11px] text-muted">Les coordonnées du destinataire s'affichent dans votre espace fleuriste une fois la commande acceptée.</p>
              </>
            ) : (
              <p className="text-sm text-ink border border-brass-light p-3 flex items-start gap-2" data-testid="accept-state">
                <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" /> {STATE_TEXT[data.state]}
              </p>
            )}
            {err && data ? <p className="text-sm text-danger" data-testid="accept-error">{err}</p> : null}
            <a href="/florist" className="text-xs underline decoration-brass text-muted" data-testid="accept-open-app">Ouvrir l'espace fleuriste →</a>
          </>
        ) : null}
      </div>
    </div>
  );
}
